'use client'

import { useEffect, useLayoutEffect, useRef, useState } from 'react'

type TypewriterWordProps = {
  /** 順に打ち替える語。先頭の語は初期表示（SSR）にも使う */
  words: string[]
  className?: string
}

type Phase = 'type' | 'hold' | 'delete'

/**
 * Figma: illust/top/hero-copy の type-slot。
 * 1 語 4 秒で「0.4s 待つ → 1 文字 0.1s で打つ → 保持（キャレット点滅）→ 1 文字 0.06s で消す」を繰り返す
 */
const TYPE_MS = 100
const DELETE_MS = 60
const START_DELAY_MS = 400
const HOLD_MS = 2500

/**
 * 語を 1 文字ずつ打っては消して切り替える。
 * 枠の幅は「次に打つ語」の幅に合わせて動かし、隣の文字が 1 文字ごとに揺れないようにする。
 * prefers-reduced-motion のときは打鍵せず、語ごと切り替える
 */
export function TypewriterWord({ words, className = '' }: TypewriterWordProps) {
  const [index, setIndex] = useState(0)
  const [count, setCount] = useState(words[0]?.length ?? 0)
  const [phase, setPhase] = useState<Phase>('hold')
  const [widths, setWidths] = useState<number[] | null>(null)
  const measureRefs = useRef<(HTMLSpanElement | null)[]>([])

  useLayoutEffect(() => {
    const elements = measureRefs.current.filter(
      (element): element is HTMLSpanElement => element !== null
    )
    const measure = () =>
      setWidths(
        elements.map((element) => element.getBoundingClientRect().width)
      )
    measure()
    // ブレークポイントでの文字サイズ変更と Web フォントの読み込み完了で幅が変わる
    const observer = new ResizeObserver(measure)
    for (const element of elements) observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const word = words[index] ?? ''
    const next = (index + 1) % words.length
    const reduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (reduced) {
      const timer = window.setTimeout(() => {
        setIndex(next)
        setCount(words[next]?.length ?? 0)
      }, START_DELAY_MS + HOLD_MS)
      return () => window.clearTimeout(timer)
    }

    if (phase === 'type') {
      const timer = window.setTimeout(() => {
        if (count < word.length) setCount(count + 1)
        else setPhase('hold')
      }, TYPE_MS)
      return () => window.clearTimeout(timer)
    }

    if (phase === 'hold') {
      const timer = window.setTimeout(() => setPhase('delete'), HOLD_MS)
      return () => window.clearTimeout(timer)
    }

    const timer = window.setTimeout(
      () => {
        if (count > 0) {
          setCount(count - 1)
        } else {
          setIndex(next)
          setPhase('type')
        }
      },
      count > 0 ? DELETE_MS : START_DELAY_MS
    )
    return () => window.clearTimeout(timer)
  }, [words, index, count, phase])

  const typed = (words[index] ?? '').slice(0, count)
  const width = widths?.[index]

  return (
    <span
      className={`relative inline-block whitespace-nowrap transition-all duration-300 ease-in-out ${className}`}
      style={width === undefined ? undefined : { width }}
    >
      {words.map((word, i) => (
        <span
          key={word}
          ref={(element) => {
            measureRefs.current[i] = element
          }}
          aria-hidden="true"
          className="invisible absolute top-0 left-0"
        >
          {word}
        </span>
      ))}
      <span aria-hidden="true">
        {/* 全部消えた瞬間も行の高さを保つ */}
        {typed || '​'}
        <span
          className={`typewriter-caret motion-reduce:hidden ${phase === 'hold' ? 'animate-caret-blink' : ''}`}
        />
      </span>
    </span>
  )
}
